/**
 * Small pure helpers for the M2 holiday screens: what the request form may offer for a
 * selection, the clash notice from the branch calendar, wording for the API's refusals and
 * relative times for notifications. Tested in requestHelpers.test.ts.
 */
import type { ApprovalItem, CalendarEntry, HolidayRuleError } from '@aroo/shared';
import { ApiError } from '../../api/client';
import { formatDateRange, localDateToIso } from './holidayMaths';
import { EMPTY, formatShortDate } from '../../lib/format';

/** The half-day toggle only makes sense on a single day. */
export function canHalfDay(start: Date | null, end: Date | null): boolean {
  if (!start) return false;
  return end === null || end.getTime() === start.getTime();
}

/** YYYY-MM for the calendar query of a date. */
export function monthOf(d: Date): string {
  return localDateToIso(d).slice(0, 7);
}

/** The months a selection touches (one or two), for the branch calendar queries. */
export function monthsOf(start: Date, end: Date): string[] {
  const a = monthOf(start);
  const b = monthOf(end);
  return a === b ? [a] : [a, b];
}

export interface ClashNotice {
  /** "Casey Bloom is also off Wed 21 – Thu 22 Oct (approved)." */
  lines: string[];
  count: number;
}

/**
 * Colleagues (never the person themselves) with a live holiday booking on the selected days.
 * Bank-holiday bookings are everyone's, so they are not a clash.
 */
export function clashNotice(
  entries: readonly CalendarEntry[],
  personId: string,
  start: Date,
  end: Date,
): ClashNotice | null {
  const s = localDateToIso(start);
  const e = localDateToIso(end);
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const x of entries) {
    if (x.personId === personId || x.kind !== 'holiday') continue;
    if (x.startDate > e || x.endDate < s) continue;
    if (seen.has(x.bookingId)) continue;
    seen.add(x.bookingId);
    lines.push(
      `${x.name} is also off ${formatDateRange(x.startDate, x.endDate)} (${x.status === 'pending' ? 'asked' : 'approved'}).`,
    );
  }
  return lines.length === 0 ? null : { lines, count: lines.length };
}

/** What to tell the person when the API refused a request or a decision. */
export function ruleErrorMessage(err: unknown): string {
  if (!(err instanceof ApiError))
    return err instanceof Error ? err.message : 'Something went wrong.';
  const d = err.details as Partial<HolidayRuleError>;
  switch (err.code) {
    case 'over_balance':
      return typeof d.short === 'number' && typeof d.remaining === 'number'
        ? `That's ${d.short} ${d.short === 1 ? 'day' : 'days'} more than the ${d.remaining} left${d.year ? ` for ${d.year}` : ''}. Pick fewer days or ask HR about an adjustment.`
        : err.message;
    case 'overlaps':
      return d.clash
        ? `Those dates overlap a ${d.clash.status} booking, ${formatDateRange(d.clash.startDate, d.clash.endDate)}.`
        : err.message;
    case 'past_dates':
      return 'Holiday in the past can only be recorded by HR.';
    case 'no_working_days':
      return 'None of those days are working days (or they are bank holidays already booked).';
    case 'already_decided':
      return err.message;
    default:
      return err.message;
  }
}

/** "just now", "5 min ago", "3 hours ago", "Yesterday", "2 days ago", "12 Sep". */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime();
  const diff = Math.max(0, now.getTime() - then);
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return formatShortDate(iso, EMPTY, now);
}

/** "asked today", "asked yesterday", "asked 3 days ago". */
export function askedLabel(waitingDays: number): string {
  if (waitingDays <= 0) return 'asked today';
  if (waitingDays === 1) return 'asked yesterday';
  return `asked ${waitingDays} days ago`;
}

/** Ids the bulk-approve checkbox may select: no clash, so a manager need not look closer. */
export function bulkSelectable(items: readonly ApprovalItem[]): string[] {
  return items.filter((i) => !i.clash).map((i) => i.id);
}

/** Sunday or Saturday counts only when the pattern says so; the calendar greys the rest. */
export function requestDateDisabled(
  day: Date,
  today: Date,
  allowPast: boolean,
  lastAllowed: Date,
): boolean {
  if (!allowPast && day.getTime() < today.getTime()) return true;
  return day.getTime() > lastAllowed.getTime();
}
